import BigNumber from 'bignumber.js';
import { getProduct } from '#/products/domain';
import { DraftLeg } from './types';

/**
 * Strategy quantity is basically the minimum quantity on any of the legs of an RFQ
 * @param legs
 */
export function calculateStrategyQty(legs: readonly DraftLeg[]) {
  let minQty = null;

  for (let index = 0; index < legs.length; index++) {
    const leg = legs[index];
    if (leg == null) continue;
    if (leg.isHedge) continue;
    if (leg.quantity == null) continue;

    if (minQty == null || BigNumber(leg.quantity).isLessThan(minQty)) {
      minQty = leg.quantity;
    }
  }

  return minQty;
}

function calculateLegRatio(
  strategyQty: string | null,
  leg: DraftLeg,
): string | null {
  if (leg.productCode == null) return null;

  const product = getProduct(leg.productCode);

  if (leg.isHedge && product.contractType === 'INVERSE') {
    return calculateInverseHedgeLegRatio(leg, strategyQty);
  }

  return calculateNonHedgeLegRatio(leg, strategyQty);
}

function calculateNonHedgeLegRatio(
  leg: DraftLeg,
  strategyQty: string | null,
): string | null {
  if (leg.quantity == null || strategyQty == null) return null;
  // Guard against division-by-zero while the user is mid-typing a
  // sub-1 quantity (e.g. "0" as the first keystroke of "0.01" on
  // Paradex). Returning null leaves the existing ratio untouched
  // until the user enters a non-zero number, which prevents the
  // NaN ratio that made matchStrategyByRatio flip to Custom.
  if (BigNumber(strategyQty).isZero()) return null;
  return BigNumber(leg.quantity).dividedBy(strategyQty).toString();
}

function calculateInverseHedgeLegRatio(
  leg: DraftLeg,
  strategyQty: string | null,
): string | null {
  if (
    leg.quantity == null ||
    leg.price == null ||
    BigNumber(leg.price).isEqualTo(0) ||
    strategyQty == null ||
    BigNumber(strategyQty).isZero()
  )
    return null;
  return BigNumber(leg.quantity)
    .dividedBy(leg.price)
    .dividedBy(strategyQty)
    .toFixed()
    .toString();
}

export default calculateLegRatio;
